import { useCallback, useEffect } from 'react';
import { captureException } from '@sentry/browser';
import { useStorageQuotaAwareHandler } from '../storageQuota/useStorageQuotaAwareHandler';
import { StorageQuotaUseCase } from '../storageQuota/useStorageQuotaAwareHandler/StorageQuotaUseCase';
import { MediaManagerSDKFile, MediaManagerSDKFolder } from '../../media-manager-sdk';
import { parseFileName } from '../../path';
import { useNotificatorConsumer } from '../useNotificator';
import {
  editInAdobeFailedNotification,
  editInAdobeFailedToOpenNotification,
} from '../../../containers/FilesManager/notifications/adobeExpress';
import { EVENT_ADOBE_EXPRESS_EDIT_RETRY, useAppEvents } from '../../../services/appEvents';
import { useUploadFilesHandlers } from '../useFilesUploader';
import { MainSource } from '../../../constants/Source';
import { ensureScriptLoaded } from '../../dom/ensureScriptLoaded';
import { useMMGRSettings } from '../../hoc/withMMGRSettings';
import { dataURItoBlob, urlToBase64 } from '../../base64';
import {
  createAdobeExpressProjectIdTag,
  extractAdobeExpressProjectIdFromTag,
  isAdobeExpressProjectIdTag,
} from '../../../constants/SystemTags';
import { BiAddMediaActionTarget, BiAddMediaClickOrigin } from '../../../constants/Bi';
import { createFilesBiOptionsMap } from '../../bi';
import { getLocale } from '../../env/getLocale';
import { FileOrigin } from '../useFilesUploader/types';
import './useAdobeExpress.types';

const supportedMimeTypesToExtensions = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
} as const;

const ensureSDKLoaded = () =>
  ensureScriptLoaded({
    src: 'https://sdk.cc-embed.adobe.com/v3/CCEverywhere.js',
    id: 'adobe-express-sdk',
  });

const showAdobeContainer = () =>
  document.querySelector<HTMLDivElement>('#adobe-express-container')?.removeAttribute('hidden');
const hideAdobeContainer = () =>
  document.querySelector<HTMLDivElement>('#adobe-express-container')?.setAttribute('hidden', '');

let isEditing = false;
let lastEditedFile: MediaManagerSDKFile;

export const useAdobeExpress = (targetFolder: MediaManagerSDKFolder) => {
  const { appEvents } = useAppEvents();
  const { notify } = useNotificatorConsumer();
  const storageQuotaAware = useStorageQuotaAwareHandler();
  const { uploadFiles } = useUploadFilesHandlers({ folderPath: [targetFolder], source: MainSource.All });

  useEffect(() => {
    // preload the SDK script
    ensureSDKLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const editImageInAdobe = useCallback(
    async (item: MediaManagerSDKFile) => {
      if (isEditing) {
        return;
      }

      try {
        showAdobeContainer();
        isEditing = true;
        lastEditedFile = item;

        // wait for the script to be loaded
        await ensureSDKLoaded();

        let ccEverywhere = window.__WIX_MEDIA_MANAGER_CC_EVERYWHERE_INSTANCE;
        if (!ccEverywhere) {
          ccEverywhere = window.__WIX_MEDIA_MANAGER_CC_EVERYWHERE_INSTANCE = await window.CCEverywhere.initialize(
            {
              clientId: '3fecfa016031462285cd6f620a5f932a', // Get from Adobe Developer Console
              appName: 'Wix',
              appVersion: { major: 1, minor: 0 },
              platformCategory: 'web',
            },
            {
              loginMode: 'delayed',
              locale: getLocale(),
              skipBrowserSupportCheck: true,
            },
          );
        }

        const tags = item.tags || [];
        const projectId = extractAdobeExpressProjectIdFromTag(tags.find(isAdobeExpressProjectIdTag));

        const callbacks = {
          onCancel: () => {
            hideAdobeContainer();
            isEditing = false;
            lastEditedFile = undefined;
          },
          onPublish: async (publishParams) => {
            hideAdobeContainer();
            isEditing = false;
            lastEditedFile = undefined;

            try {
              const { name } = parseFileName(item.name);
              const projectIdTag = publishParams.projectId
                ? createAdobeExpressProjectIdTag(publishParams.projectId)
                : undefined;

              const asset = publishParams.asset[0];
              const extension = supportedMimeTypesToExtensions[asset.fileType];

              if (!extension) {
                throw new Error(`[adobe express]: unsupported file type: ${asset.fileType}`);
              }

              const blob = await dataURItoBlob(asset.data);
              const file = new File([blob], `${name}_edited${extension ? '.' + extension : ''}`, { type: blob.type });
              uploadFiles([file], undefined, {
                ...(projectIdTag ? { tags: [projectIdTag] } : undefined),
                bi: {
                  files: createFilesBiOptionsMap(
                    [file],
                    BiAddMediaClickOrigin.AdobeExpress,
                    BiAddMediaActionTarget.AdobeExpress,
                    FileOrigin.AdobeExpress,
                  ),
                },
              });

              if (!projectIdTag) {
                captureException(new Error('[adobe express]: no project id to store in tags'));
              }
            } catch (error) {
              notify(editInAdobeFailedNotification(error));
              captureException(new Error(`[adobe express] onPublish: ${error.message}`, { cause: error }));
              throw error;
            }
          },
          onError: (error: Error) => {
            hideAdobeContainer();
            isEditing = false;
            notify(editInAdobeFailedNotification(error));
            captureException(new Error(`[adobe express] onError: ${error.message}`, { cause: error }));
            throw error;
          },
        };

        const modalParams = {
          parentElementId: 'adobe-express-container',
        };

        const inputParams = {
          editorPanelView: 'search',
        } as const;

        const outputParams = {
          outputType: 'base64',
          multiPage: false,
          imageQuality: 1,
          allowedFileTypes: Object.keys(supportedMimeTypesToExtensions),
        };

        if (projectId) {
          ccEverywhere.editDesign({
            callbacks,
            modalParams,
            outputParams,
            inputParams: { ...inputParams, projectId },
          });
        } else {
          // Unsplash items have fileUrl rather than originalUrl
          const url = item.originalUrl || item.fileUrl;
          if (!url) {
            throw new Error(`[adobe express]: Cannot edit file in adobe express. ID: ${item.id}`);
          }

          ccEverywhere.createDesign({
            callbacks,
            modalParams,
            outputParams,

            inputParams: {
              ...inputParams,
              canvasSize: { width: item.width, height: item.height, unit: 'px' },
              asset: {
                type: 'image',
                dataType: 'base64',
                data: await urlToBase64(url),
              },
            },
          });
        }
      } catch (error) {
        isEditing = false;
        notify(editInAdobeFailedToOpenNotification());
        hideAdobeContainer();
        captureException(new Error(`[adobe express] failed to open: ${error.message}`, { cause: error }));
        throw error;
      }
    },
    [notify, uploadFiles],
  );

  const retryLastEditInAdobe = storageQuotaAware(
    useCallback(() => {
      if (lastEditedFile) {
        editImageInAdobe(lastEditedFile);
      }
    }, [editImageInAdobe]),
    StorageQuotaUseCase.ADOBE_EXPRESS,
  );

  useEffect(() => {
    appEvents.on(EVENT_ADOBE_EXPRESS_EDIT_RETRY, retryLastEditInAdobe);
    return () => appEvents.off(EVENT_ADOBE_EXPRESS_EDIT_RETRY, retryLastEditInAdobe);
  }, [appEvents, retryLastEditInAdobe]);

  return {
    editImageInAdobe: storageQuotaAware(editImageInAdobe, StorageQuotaUseCase.ADOBE_EXPRESS),
  };
};
